import { ResourceItem } from '../../models/interfaces';

interface Props {
    resources: ResourceItem[];
    selected: number[];
    onChange: (selected: number[]) => void;
    readOnly?: boolean;
}

/**
 * Permisos agrupados por menú. Solo se pueden marcar los que quien edita puede otorgar;
 * al marcar una opción se marca su menú y al desmarcar un menú se desmarcan sus opciones.
 */
const ResourceTree = ({ resources, selected, onChange, readOnly = false }: Props) => {
    const parents = resources.filter(resource => !resource.parentId);
    const childrenOf = (id: number) => resources.filter(resource => resource.parentId === id);
    const isSelected = (id: number) => selected.includes(id);

    const toggle = (resource: ResourceItem) => {
        if (readOnly || !resource.grantable) return;
        const next = new Set(selected);
        if (next.has(resource.id)) {
            next.delete(resource.id);
            childrenOf(resource.id).forEach(child => next.delete(child.id));
        } else {
            next.add(resource.id);
            const parent = resources.find(item => item.id === resource.parentId);
            if (parent?.grantable) next.add(parent.id);
        }
        onChange(Array.from(next));
    };

    // Solo se muestran los permisos que puede otorgar o que el grupo ya tiene
    const visible = (resource: ResourceItem) =>
        resource.grantable || isSelected(resource.id) || childrenOf(resource.id).some(child => child.grantable || isSelected(child.id));

    const item = (resource: ResourceItem, child = false) => (
        <div className={`form-check ${child ? 'ms-4' : ''}`} key={resource.id}>
            <input
                className="form-check-input"
                type="checkbox"
                id={`resource-${resource.id}`}
                checked={isSelected(resource.id)}
                disabled={readOnly || !resource.grantable}
                onChange={() => toggle(resource)}
            />
            <label className={`form-check-label ${child ? '' : 'fw-medium'} ${resource.grantable ? '' : 'text-muted'}`} htmlFor={`resource-${resource.id}`}>
                {resource.name}
                {!resource.grantable && <i className="bi bi-lock ms-1 small" title="No puede otorgar este permiso" />}
            </label>
        </div>
    );

    const shown = parents.filter(visible);
    if (shown.length === 0) {
        return <div className="text-muted small">No hay permisos que pueda otorgar en este nivel.</div>;
    }

    return (
        <div className="row g-3">
            {shown.map(parent => (
                <div className="col-md-6" key={parent.id}>
                    <div className="border rounded-3 p-2 h-100">
                        {item(parent)}
                        {childrenOf(parent.id).filter(visible).map(child => item(child, true))}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default ResourceTree;
